import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, Text, LoadingOrbit, Checkbox, TooltipTrigger, Tooltip, DocsAnnotation } from '@chatsdet/ui';
import { Info } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useWorkflowDetail } from '../../../hooks/useWorkflows';
import { normalizeConcreteWorkflowId } from './workflowIds';
import { MacroRichTextField } from './MacroRichTextField';

type CallFlowNodeConfigProps = {
  isActive: boolean;
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  workflowId?: string;
  /**
   * Commit node data straight away instead of waiting for the panel's deferred
   * save. Used when picking the child flow, because the canvas card titles
   * itself from `flowName` — same reason the seed and setup panels take it.
   */
  onImmediateUpdate?: (data: Record<string, any>) => void;
};

interface CallableFlow {
  id: string;
  name: string;
  snapshotVersion: number;
  publishedAt: string | null;
}

interface Snapshot {
  version: number;
  publishedAt: string;
  publishedBy: string | null;
}

interface FlowValueInput {
  kind: 'value';
  name: string;
  label: string;
  required?: boolean;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
}

/**
 * Config panel for `call_flow` nodes — runs another flow at this node's
 * position in the chain. Modeled closely on `SeedNodeConfig`: a flow picker
 * plus a per-input field loop driven by the selected flow's declared inputs.
 *
 * Two deliberate differences from `SeedNodeConfig`:
 * - The flow picker is NOT filtered by `flowType` — a call_flow node can call
 *   any flow, not just seed scripts. It IS still restricted to eligible/
 *   promoted, non-archived, non-shadow flows (the same `setup`/`seed` rule:
 *   don't let a verification step call an unvetted draft), via
 *   `GET /workflows/setup` → `listEligibleFlowsForSetup` /
 *   `listPromotedFlowsForSetup`.
 * - Input values are rendered with `MacroRichTextField` (not `TextField`)
 *   because they are interpolated against the parent's variables at enqueue
 *   time, so a value may legitimately contain `$var.x`.
 */
export const CallFlowNodeConfig: React.FC<CallFlowNodeConfigProps> = ({
  isActive,
  localData,
  setLocalData,
  workflowId,
  onImmediateUpdate,
}) => {
  const flowId = normalizeConcreteWorkflowId(localData.flowId);
  const hasConcreteFlow = flowId !== null;

  // Fetch callable flows for the org (excluding the current workflow, and NOT
  // filtered by flowType — a call_flow node can call any flow. Still limited
  // to eligible/promoted, non-archived, non-shadow flows — a draft flow won't
  // appear until it's promoted.
  const flowsQ = useQuery({
    queryKey: ['call-flow-flows', workflowId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (workflowId) params.set('excludeWorkflowId', workflowId);
      const r = await fetch(`${API_URL}/workflows/setup?${params.toString()}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed to fetch flows');
      const data = await r.json();
      return (data.workflows || []) as CallableFlow[];
    },
    enabled: isActive,
  });

  // Fetch available snapshots for the selected flow
  const snapshotsQ = useQuery({
    queryKey: ['node-config-snapshots', flowId],
    queryFn: async () => {
      if (!flowId) return [];
      const r = await fetch(`${API_URL}/workflows/${flowId}/snapshots`, {
        credentials: 'include',
      });
      if (r.status === 404) return [];
      if (!r.ok) throw await toApiError(r, 'Failed to fetch snapshots');
      const data = await r.json();
      return (data.snapshots || []) as Snapshot[];
    },
    enabled: isActive && hasConcreteFlow,
  });

  // Fetch the selected flow's declared value inputs, for the per-node fields below.
  //
  // Distinct key despite hitting the same endpoint as the outputs hooks: a
  // React Query key names a CACHED SHAPE, not a URL. `useCallFlowNodeOutputs`
  // and `useSeedNodeOutputs` store the raw `SeedOutputsResponse`
  // (`{ ok, outputs, workflowName? }`, see useSeedWorkflowOutputs.ts) under
  // `['workflow', id, 'seed-outputs']`; this query projects `data.inputs` into
  // a `FlowValueInput[]`. Sharing the key would let whichever fetch resolved
  // first decide what the other reads — here that means `flowInputsQ.data`
  // silently becoming an object, so the Inputs fields never render at all.
  const flowInputsQ = useQuery({
    queryKey: ['workflow', flowId, 'call-flow-inputs'],
    queryFn: async () => {
      if (!flowId) return [];
      const r = await fetch(`${API_URL}/workflows/${flowId}/seed-outputs`, {
        credentials: 'include',
      });
      if (r.status === 404) return [];
      if (!r.ok) throw await toApiError(r, 'Failed to fetch flow inputs');
      const data = await r.json();
      return (data.inputs || []) as FlowValueInput[];
    },
    enabled: isActive && hasConcreteFlow,
  });

  // The parent workflow's available environments — shares the cache with
  // FlowDesigner's own `useWorkflowDetail(['workflow-detail', workflowId])`
  // query, so both observers read from the same `queryFn`.
  const workflowDetailQ = useWorkflowDetail(workflowId ?? null);
  const environments = workflowDetailQ.data?.environments ?? [];

  const flowOptions = (flowsQ.data || []).map((flow) => ({
    id: flow.id,
    label: flow.name,
  }));

  const snapshotOptions = [
    { id: 'latest', label: 'Live (latest definition)' },
    ...(snapshotsQ.data || []).map((snapshot) => ({
      id: String(snapshot.version),
      label: `v${snapshot.version} (${new Date(snapshot.publishedAt).toLocaleDateString()})`,
    })),
  ];

  const environmentOptions = [
    { id: 'auto', label: 'Auto (resolve at run time)' },
    ...environments.map((env) => ({ id: env.id, label: env.name })),
  ];

  const flowValueInputs = flowInputsQ.data ?? [];

  return (
    <DocsAnnotation slug="reference/flows" title="Run a Flow Nodes" className="min-h-full">
      <div className="space-y-4">
        <Text className="text-sm text-muted-foreground">
          Select a flow to run at this point in the chain. Its declared outputs become available
          as $var.* to later steps.
        </Text>

        {flowsQ.isLoading ? (
          <LoadingOrbit />
        ) : flowOptions.length === 0 ? (
          <div className="p-4 rounded-lg bg-muted/50 space-y-2">
            <Text size="sm" className="text-muted-foreground font-medium">
              No other flows available to call.
            </Text>
            <Text size="sm" className="text-muted-foreground">
              A flow becomes callable once it is approved and has passed a run. Drafts don't
              appear here.
            </Text>
          </div>
        ) : (
          <>
            <Select
              label="Flow"
              placeholder="Select a flow"
              items={flowOptions}
              selectedKey={flowId}
              onSelectionChange={(key) => {
                const flow = flowsQ.data?.find((f) => f.id === key);
                const next = {
                  ...localData,
                  flowId: key as string,
                  flowName: flow?.name ?? null,
                  snapshotVersion: null,
                  inputValues: {},
                  // environmentId/executionMode/onFailure are required-but-nullable
                  // fields on CallFlowNodeConfig — write explicit defaults now so a
                  // save right after picking a flow (before touching every other
                  // control) still persists a complete config, not an omission.
                  environmentId: localData.environmentId ?? null,
                  executionMode: localData.executionMode ?? 'recommended',
                  onFailure: localData.onFailure ?? 'block',
                };
                setLocalData(next);
                // Commit immediately, as the seed and setup panels do for the
                // same control. The canvas card titles itself from `flowName`,
                // so without this it keeps reading "Select a flow" until the
                // panel closes and the deferred save flushes.
                onImmediateUpdate?.(next);
              }}
            />

            {hasConcreteFlow && snapshotsQ.isLoading && <LoadingOrbit />}

            {hasConcreteFlow && !snapshotsQ.isLoading && snapshotOptions.length > 0 && (
              <Select
                label="Version"
                placeholder="Select a version"
                items={snapshotOptions}
                selectedKey={
                  localData.snapshotVersion === null || localData.snapshotVersion === undefined
                    ? 'latest'
                    : String(localData.snapshotVersion)
                }
                onSelectionChange={(key) => {
                  setLocalData({
                    ...localData,
                    snapshotVersion: key === 'latest' ? null : Number(key),
                  });
                }}
              />
            )}

            {hasConcreteFlow && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Select
                    label="Environment"
                    className="flex-1"
                    items={environmentOptions}
                    selectedKey={localData.environmentId ?? 'auto'}
                    onSelectionChange={(key) => {
                      setLocalData({
                        ...localData,
                        environmentId: key === 'auto' ? null : (key as string),
                      });
                    }}
                  />
                  <TooltipTrigger delay={100}>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors mt-6"
                    >
                      <Info size={14} />
                    </button>
                    <Tooltip>
                      Environment the called flow runs in. Leave on Auto to let the handler
                      resolve one.
                    </Tooltip>
                  </TooltipTrigger>
                </div>
              </div>
            )}

            {hasConcreteFlow && (
              <div className="space-y-1">
                <Select
                  label="Execution Mode"
                  items={[
                    { id: 'recommended', label: 'Recommended (API when available)' },
                    { id: 'api', label: 'API Only' },
                    { id: 'browser', label: 'Browser' },
                  ]}
                  selectedKey={localData.executionMode ?? 'recommended'}
                  onSelectionChange={(key) => {
                    setLocalData({
                      ...localData,
                      executionMode: key as string,
                    });
                  }}
                />
                {(localData.executionMode ?? 'recommended') === 'recommended' && (
                  <Text size="xs" className="text-muted-foreground">
                    Automatically uses API execution if the flow has an API sequence, otherwise
                    falls back to browser.
                  </Text>
                )}
              </div>
            )}

            {hasConcreteFlow && flowValueInputs.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Text size="sm" className="font-medium">
                    Inputs
                  </Text>
                  <TooltipTrigger delay={100}>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Info size={14} />
                    </button>
                    <Tooltip>
                      Set values for this flow&apos;s declared inputs. Values may reference
                      $var.* from this workflow.
                    </Tooltip>
                  </TooltipTrigger>
                </div>
                {flowValueInputs.map((input) => (
                  <MacroRichTextField
                    key={input.name}
                    label={`${input.label || input.name}${input.required ? ' *' : ''}`}
                    placeholder={input.placeholder ?? input.defaultValue ?? ''}
                    description={input.description}
                    value={localData.inputValues?.[input.name] ?? ''}
                    onChange={(value) => {
                      const next: Record<string, string> = { ...localData.inputValues };
                      if (value === '') {
                        delete next[input.name];
                      } else {
                        next[input.name] = value;
                      }
                      setLocalData({
                        ...localData,
                        inputValues: next,
                      });
                    }}
                  />
                ))}
              </div>
            )}

            {hasConcreteFlow && (
              <div className="flex items-center gap-2">
                <Checkbox
                  isSelected={localData.onFailure === 'warn'}
                  onChange={(checked) => {
                    setLocalData({
                      ...localData,
                      onFailure: checked ? 'warn' : 'block',
                    });
                  }}
                  label="Continue on failure"
                />
                <TooltipTrigger delay={100}>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Info size={14} />
                  </button>
                  <Tooltip>
                    If checked, the workflow will continue even if the called flow fails — but a
                    warned call publishes no outputs to later steps.
                  </Tooltip>
                </TooltipTrigger>
              </div>
            )}
          </>
        )}
      </div>
    </DocsAnnotation>
  );
};
